import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, MapPin, Recycle, RefreshCw } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FacilityVerificationBadge } from '@/components/disposal/FacilityVerificationBadge';

const facilityTypes = ['dump', 'donation', 'donation_box', 'recycling', 'scrap'];
const materials = ['general_junk', 'yard_waste', 'construction_debris', 'metal', 'appliances', 'electronics', 'cardboard', 'paper', 'plastic', 'glass', 'textiles', 'furniture', 'mattress', 'tires', 'hazardous'];
const typeLabels = { dump: 'Dump / Transfer', donation: 'Donation Center', donation_box: 'Donation Box', recycling: 'Recycling', scrap: 'Scrap Yard' };
const typeColor = { dump: 'border-zinc-300 bg-zinc-100 text-zinc-800', donation: 'border-blue-200 bg-blue-50 text-blue-700', donation_box: 'border-cyan-200 bg-cyan-50 text-cyan-800', recycling: 'border-emerald-200 bg-emerald-50 text-emerald-700', scrap: 'border-amber-200 bg-amber-50 text-amber-800' };

const materialLabel = (value) => String(value || '').replaceAll('_', ' ');
const mapsLink = (facility) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([facility?.name, facility?.address].filter(Boolean).join(' '))}`;

export function FacilityLookupPanel({ compact = false, defaultMaterial = 'all', testId = 'facility-lookup-panel' }) {
  const [facilities, setFacilities] = useState([]);
  const [facilityType, setFacilityType] = useState('all');
  const [material, setMaterial] = useState(defaultMaterial);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/disposal/facilities', { params: { facility_type: facilityType, material } });
      setFacilities(data || []);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [facilityType, material]);

  useEffect(() => { fetchFacilities(); }, [fetchFacilities]);

  const visibleFacilities = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return facilities;
    return facilities.filter((facility) => [facility.name, facility.address, facility.notes, ...(facility.accepted_materials || [])].join(' ').toLowerCase().includes(needle));
  }, [facilities, search]);

  return (
    <div className="rounded-sm border border-zinc-200 bg-white p-3" data-testid={testId}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <p className="flex items-center gap-1 text-sm font-black text-zinc-950"><Recycle className="h-4 w-4" /> Disposal Lookup</p>
          <p className="text-xs text-zinc-500" data-testid={`${testId}-summary`}>{visibleFacilities.length} nearby disposal options</p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={fetchFacilities} disabled={loading} data-testid={`${testId}-refresh-button`}><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-3" data-testid={`${testId}-filters`}>
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search locations" className="h-9" data-testid={`${testId}-search-input`} />
        <select value={facilityType} onChange={(event) => setFacilityType(event.target.value)} className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-xs" data-testid={`${testId}-type-filter`}><option value="all">All types</option>{facilityTypes.map((type) => <option key={type} value={type}>{typeLabels[type]}</option>)}</select>
        <select value={material} onChange={(event) => setMaterial(event.target.value)} className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-xs" data-testid={`${testId}-material-filter`}><option value="all">All items</option>{materials.map((item) => <option key={item} value={item}>{materialLabel(item)}</option>)}</select>
      </div>
      {error && <p className="mt-2 rounded-sm border border-red-200 bg-red-50 p-2 text-xs text-red-700" data-testid={`${testId}-error`}>{error}</p>}
      <div className={`mt-3 space-y-2 ${compact ? 'max-h-72 overflow-y-auto pr-1' : ''}`} data-testid={`${testId}-list`}>
        {!error && visibleFacilities.length === 0 && <p className="text-xs text-zinc-500" data-testid={`${testId}-empty`}>{loading ? 'Loading disposal locations...' : 'No matching disposal locations.'}</p>}
        {visibleFacilities.slice(0, compact ? 8 : 20).map((facility, index) => (
          <div key={facility.id || `${facility.name}-${index}`} className="rounded-sm border border-zinc-200 p-2" data-testid={`${testId}-item-${index}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-zinc-950" data-testid={`${testId}-item-name-${index}`}>{facility.name}</p>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-zinc-500" data-testid={`${testId}-item-address-${index}`}><MapPin className="h-3 w-3 shrink-0" /> {facility.address || 'Address not saved'}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1"><Badge className={typeColor[facility.facility_type] || ''} data-testid={`${testId}-item-type-${index}`}>{typeLabels[facility.facility_type] || facility.facility_type}</Badge><FacilityVerificationBadge facility={facility} testId={`${testId}-item-verification-badge-${index}`} /></div>
            </div>
            <p className="mt-1 text-xs text-zinc-500" data-testid={`${testId}-item-materials-${index}`}>Accepts: {(facility.accepted_materials || []).map(materialLabel).join(', ') || 'Verify manually'}</p>
            <div className="mt-2 flex items-center justify-between gap-2 text-xs">
              <span data-testid={`${testId}-item-distance-${index}`}>{Number(facility.distance_miles || 0).toFixed(1)} miles away</span>
              <a href={mapsLink(facility)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-zinc-900" data-testid={`${testId}-item-google-link-${index}`}>Maps <ExternalLink className="h-3 w-3" /></a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}